import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { donedealServicesGateway } from '../../api/api-utils/commonApiUtils';

import { useLastSearch } from '../../components/PageWrappers/SharedComponents/LastSearchCard/hooks/useLastSearch';
import { useAuthOptions } from '../../hooks/useAuthOptions';

import { setAuthorizationHeader } from '../../api/api-utils/authenticate';

import type { SavedSearch } from '../../types';

import type { PreviousSearchesArgs } from '../../features/PreviousSearches/PreviousSearches.typed';
import { AUTHENTICATION_STATUSES } from '../../components/PageWrappers/AdDetails/types';

const usePreviousSearches = (args: PreviousSearchesArgs) => {
  const { onSearchCallback, page } = args;

  const { status, accessToken, handleLogin } = useAuthOptions();
  const { lastSearchData } = useLastSearch();

  /*
   * We set this to true when opening the modal and false on close of the modal
   * to avoid fetching when the modal is on screen, or when there is an error.
   *
   * If there is an error, we set this to true only if the user decides to
   * refetch data again.
   *
   * This limits network requests to when the user wishes to see the data in the
   * interest of performance.
   * */
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isPreviousSearchesModalOpen, setIsPreviousSearchesModalOpen] =
    useState(false);

  const showSearch = Boolean(lastSearchData);
  const isEnabled =
    status === AUTHENTICATION_STATUSES.AUTHENTICATED || showSearch;
  const isLoading = status === AUTHENTICATION_STATUSES.LOADING;

  const getSavedSearches = async (
    url: string,
    token: string,
  ): Promise<Array<SavedSearch>> => {
    const { data } = await donedealServicesGateway.get(url, {
      headers: {
        Authorization: setAuthorizationHeader(token),
      },
    });

    return data;
  };

  const {
    isPending,
    data: savedSearchesData,
    refetch: onSavedSearchesError,
    isError: savedSearchesError,
  } = useQuery({
    queryKey: ['previous-searches-modal-saved-searches'],
    queryFn: () => {
      if (accessToken) {
        return getSavedSearches(
          '/ddapi/v1/users/savedsearches?page=0&size=3',
          accessToken,
        );
      }
    },
    retry: false,
    /*
     * Should only fetch when user clicks on button to display previous searches modal
     * and not when the component mounts
     */
    enabled: shouldFetch,
    meta: {
      tags: [page],
      noToast: true,
      errorMessage: 'Error getting previous searches',
    },
  });

  const isSavedSearchesLoading =
    isPending && status === AUTHENTICATION_STATUSES.AUTHENTICATED;
  const savedSearches =
    savedSearchesData && savedSearchesData.length > 0
      ? savedSearchesData
      : undefined;

  const onClick = () => {
    if (isEnabled) {
      setIsPreviousSearchesModalOpen(true);
    }
    if (status === AUTHENTICATION_STATUSES.AUTHENTICATED) {
      setShouldFetch(true);
    }
  };

  const askToClose = () => {
    setShouldFetch(false);
    setIsPreviousSearchesModalOpen(false);
  };

  const login = () => handleLogin();

  const onSearch = () => {
    askToClose();
    onSearchCallback && onSearchCallback();
  };

  return {
    isLoading,
    isDisabled: !isEnabled,
    onClick,
    isSavedSearchesLoading,
    savedSearches,
    askToClose,
    savedSearchesError,
    onSavedSearchesError,
    isOpen: isPreviousSearchesModalOpen,
    login,
    status,
    onSearch,
    lastSearchData,
  };
};

export { usePreviousSearches };
